import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAnimatedStyle, useDerivedValue, useSharedValue } from 'react-native-reanimated';
import { Gesture } from 'react-native-gesture-handler';
import { ImagePickerAsset } from 'expo-image-picker';
import { ImageResult, ImageManipulator, SaveFormat } from 'expo-image-manipulator';

export interface CropPhotoParams {
	imageToCrop: ImagePickerAsset | null;
	onSave: (croppedImage: ImageResult) => void;
}

export const AVATAR_CIRCLE_DIAMETER = 300;

export const useCropProfilePhoto = ({ imageToCrop, onSave }: CropPhotoParams) => {
	const offset = useSharedValue({ x: 0, y: 0 });
	const [scale, setScale] = useState(1);

	useEffect(() => {
		const updatedScale = imageToCrop ? getScaleAdjustedToAvatarCircle(imageToCrop) : 1;
		setScale(updatedScale);
		offset.value = { x: 0, y: 0 };
	}, [imageToCrop, offset]);

	const cropImage = useCallback(async () => {
		if (!imageToCrop) return;
		const imageCenter = {
			x: imageToCrop.width / 2,
			y: imageToCrop.height / 2,
		};

		const scaledCropDiameter = AVATAR_CIRCLE_DIAMETER / scale;
		const scaledOffsetX = offset.value.x / scale;
		const scaledOffsetY = offset.value.y / scale;
		const originX = imageCenter.x - scaledOffsetX - scaledCropDiameter / 2;
		const originY = imageCenter.y - scaledOffsetY - scaledCropDiameter / 2;

		const imageManipulator = ImageManipulator.manipulate(imageToCrop.uri).crop({
			originX,
			originY,
			width: AVATAR_CIRCLE_DIAMETER / scale,
			height: AVATAR_CIRCLE_DIAMETER / scale,
		});

		const croppedImage = await (
			await imageManipulator.renderAsync()
		).saveAsync({ format: SaveFormat.PNG, base64: true });

		onSave(croppedImage);
	}, [imageToCrop, onSave, offset.value, scale]);

	const minScale = useMemo(() => {
		if (!imageToCrop) {
			return 1;
		}

		return getScaleAdjustedToAvatarCircle(imageToCrop);
	}, [imageToCrop]);

	const maxScale = useMemo(() => {
		if (!imageToCrop) {
			return 1;
		}

		return getMaxScale(imageToCrop);
	}, [imageToCrop]);

	const maxTranslateDistance = useDerivedValue(() => {
		if (!imageToCrop) {
			return {
				x: 0,
				y: 0,
			};
		}
		const { width, height } = imageToCrop;

		return {
			x: getMaxTranslateDistance(width, scale),
			y: getMaxTranslateDistance(height, scale),
		};
	});

	const panGesture = Gesture.Pan().onChange((event) => {
		const { changeX, changeY } = event;
		const newOffsetX = offset.value.x + changeX;
		const newOffsetY = offset.value.y + changeY;
		const { x: maxTranslateX, y: maxTranslateY } = maxTranslateDistance.value;

		offset.value = {
			x: getNewOffsetOrMax(newOffsetX, maxTranslateX),
			y: getNewOffsetOrMax(newOffsetY, maxTranslateY),
		};
	});

	const animatedStyles = useAnimatedStyle(() => ({
		transform: [{ translateX: offset.value.x }, { translateY: offset.value.y }, { scale: scale }],
	}));

	const handleScaleChange = (value: number) => {
		if (value < minScale || value > maxScale) {
			return;
		}
		if (!imageToCrop) return;
		const maxTranslateX = getMaxTranslateDistance(imageToCrop.width, value);
		const maxTranslateY = getMaxTranslateDistance(imageToCrop.height, value);

		offset.value = {
			x: getNewOffsetOrMax(offset.value.x, maxTranslateX),
			y: getNewOffsetOrMax(offset.value.y, maxTranslateY),
		};

		setScale(value);
	};

	const scaleStep = useDerivedValue(() => {
		return maxScale / 20;
	});

	const stepUpScale = () => {
		handleScaleChange(scale + scaleStep.value);
	};
	const stepDownScale = () => {
		handleScaleChange(scale - scaleStep.value);
	};

	const scalingDisabled = maxScale <= 1;

	return {
		cropImage,
		minScale,
		maxScale,
		animatedStyles,
		panGesture,
		handleScaleChange,
		scaleValue: scale,
		scaleStep: scaleStep.value,
		stepUpScale,
		stepDownScale,
		scalingDisabled,
	};
};

const getNewOffsetOrMax = (currentOffset: number, maxOffset: number) => {
	if (Math.abs(currentOffset) <= maxOffset) {
		return currentOffset;
	}

	return currentOffset < 0 ? -maxOffset : maxOffset;
};

const getMaxTranslateDistance = (dimension: number, scale: number) => {
	return (dimension * scale - AVATAR_CIRCLE_DIAMETER) / 2;
};

const getScaleAdjustedToAvatarCircle = (asset: ImagePickerAsset) => {
	const smallerDimension = getAssetSmallerDimension(asset);

	return AVATAR_CIRCLE_DIAMETER / smallerDimension;
};

const getMaxScale = (asset: ImagePickerAsset) => {
	const smallerDimension = getAssetSmallerDimension(asset);
	if (smallerDimension <= AVATAR_CIRCLE_DIAMETER) {
		return 1;
	}

	return smallerDimension / AVATAR_CIRCLE_DIAMETER;
};

const getAssetSmallerDimension = (asset: ImagePickerAsset) => {
	const { width, height } = asset;

	return Math.min(width, height);
};
